import { RequestTypeOf } from "../generated/routes";
import { Building, CreateBuilding } from "../generated/types";
import { I18nKey } from "../i18n/i18n";
// Admin/psc voient tous les bâtiments (route admin) ; les autres types voient ceux de leur
// entreprise (route scopée `getBuildings` = GET /buildings → GetBuildingsOfUser côté DATA).
import { isAdminAccount } from "../utils/roles";
import { apiFetchJson, apiFetchRoute } from "./apiFetchRoute";

export const fetchBuildingById = async (
  id: number
): Promise<{
  building: Building | null;
  error: string | null;
}> => {
  try {
    const building = await apiFetchJson("getBuildingId", undefined, {
      params: { id },
    });

    return { building: building ?? null, error: null };
  } catch (err) {
    console.error("Erreur:", err);
    return { building: null, error: "Impossible de charger le bâtiment" };
  }
};

export const fetchBuildings = async (): Promise<{
  Buildings: Building[];
  error: string | null;
}> => {
  try {
    const data = isAdminAccount()
      ? await apiFetchJson("getAdminBuildings", undefined)
      : await apiFetchJson("getBuildings", undefined);

    // Le backend renvoie `null` (slice Go nil) quand l'utilisateur n'a aucun bâtiment ;
    // sans ce garde, un `null` remonte tel quel et fait planter `buildings.length`/`.map` (écran blanc).
    return { Buildings: data ?? [], error: null };
  } catch (err) {
    console.error("Erreur:", err);
    return { Buildings: [], error: "Impossible de charger les bâtiments" };
  }
};

/**
 * Bâtiments d'une entreprise DONNÉE, pour le sélecteur d'association d'un produit.
 *
 * Distincte de `fetchBuildings`, qui lit l'entreprise de la session : un installateur
 * associe un produit CHEZ SON CLIENT et a donc besoin des bâtiments d'une entreprise qui
 * n'est pas la sienne.
 *
 * Le backend applique les MÊMES droits que l'association elle-même (403 sinon) : la route
 * n'est pas un annuaire ouvert des bâtiments de n'importe quelle entreprise.
 */
export const fetchBuildingsOfCompany = async (
  idCompany: number
): Promise<{ buildings: Building[]; error: string | null }> => {
  try {
    const data = await apiFetchJson("getBuildingsCompanyId_company", undefined, {
      params: { id_company: idCompany },
    });

    // Même garde que fetchBuildings : une slice Go vide sérialise parfois en `null`.
    return { buildings: data ?? [], error: null };
  } catch (err) {
    console.error("Erreur:", err);
    return { buildings: [], error: "Impossible de charger les bâtiments" };
  }
};

export const createBuilding = async (
  formData: CreateBuilding
): Promise<{
  success: boolean;
  error?: string | null;
}> => {
  try {
    type BuildingRequest = RequestTypeOf<"postBuildings">;

    const body: BuildingRequest = formData;
    console.log(body);

    const BuildingResponse = await apiFetchRoute("postBuildings", body, {
      headers: {
        "Content-type": "application/json",
      },
    });

    if (!BuildingResponse.ok) {
      return {
        success: false,
        error: "Une erreur est survenue lors de la création du bâtiment",
      };
    }

    return {
      success: true,
    };
  } catch (error) {
    console.error("Erreur lors de la création du bâtiment:", error);
    return {
      success: false,
      error: "Une erreur est survenue lors de la création du bâtiment",
    };
  }
};

export const updateBuilding = async (
  formData: Building
): Promise<{
  success: boolean;
  error?: string | null;
}> => {
  try {
    type BuildingRequest = RequestTypeOf<"putBuildings">;

    const body: BuildingRequest = formData;
    console.log(body);

    const BuildingResponse = await apiFetchRoute("putBuildings", body, {
      headers: {
        "Content-type": "application/json",
      },
    });

    if (!BuildingResponse.ok) {
      return {
        success: false,
        error: "Une erreur est survenue lors de la modification du bâtiment",
      };
    }

    return {
      success: true,
    };
  } catch (error) {
    console.error("Erreur lors de la modification du bâtiment:", error);
    return {
      success: false,
      error: "Une erreur est survenue lors de la modification du bâtiment",
    };
  }
};

/**
 * Supprime un bâtiment. L'API refuse (409) tant qu'il a des produits ou des lots.
 * Rend la clé du message d'erreur, vide en cas de succès.
 */
export const deleteBuilding = async (id: number): Promise<I18nKey | ""> => {
  try {
    const response = await apiFetchRoute("deleteBuildingsId", undefined, {
      params: { id },
    });
    if (response.ok) return "";
    // i18n.t("building-delete-failed")
    return response.status === 409
      ? "building-delete-not-empty"
      : "building-delete-failed";
  } catch (error) {
    console.error("Erreur lors de la suppression du bâtiment:", error);
    return "building-delete-failed";
  }
};
